// src/components/MessageBubble.tsx - FIXED VERSION WITH ERROR HANDLING
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Message, MessageType } from '../types/Message';
import '../assets/styles/MessageBubble.css';

interface MessageBubbleProps {
  message: Message;
  onImageFeedback?: (imageId: string, rating: number, feedback?: string) => void;
  emcogName?: string;
  userName?: string;
}

// ADD THIS PREPROCESSING FUNCTION
const preprocessContent = (content: string): string => {
  // Don't process empty content
  if (!content) return content;

  // Split content into lines
  const lines = content.split('\n');
  const processedLines: string[] = [];

  for (const line of lines) {
    // Check if line starts with an italic action (e.g., *snap* Some text)
    const actionMatch = line.match(/^(\*[^*]+\*)\s+(.+)$/);

    if (actionMatch) {
      const action = actionMatch[1];
      const restOfLine = actionMatch[2];

      // Add the action as its own line
      processedLines.push(action);
      // Add empty line for spacing
      processedLines.push('');
      // Add the rest of the content
      processedLines.push(restOfLine);
    } else {
      // Regular line, just add it
      processedLines.push(line);
    }
  }

  let joined = processedLines.join('\n');

  // Collapse numbered-list items where the LLM put a newline between the
  // marker and the content (e.g. "1.\n**Quora Digest** ..."). With remarkBreaks
  // enabled every \n becomes a hard <br>, so the number ends up alone on its
  // own line and the bold content drops below it. Folding "1.\n" → "1. "
  // restores the intended single-line list-item layout. Only fires when the
  // marker line has no other content.
  joined = joined.replace(/^(\s*\d+\.)[ \t]*\r?\n[ \t]+/gm, '$1 ');
  // Same for bullets ("- \n  text" or "* \n  text")
  joined = joined.replace(/^(\s*[-*])[ \t]*\r?\n[ \t]+/gm, '$1 ');

  return joined;
};

// 🔥 NEW: Download media functionality
const downloadMedia = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);
    console.log('✅ Downloaded:', filename);
  } catch (error) {
    console.error('❌ Download failed:', error);
    alert('Download failed. Please try again.');
  }
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onImageFeedback, emcogName = 'EmCog', userName = 'User' }) => {
  // Ensure content is always a string
  const safeContent = String(message.content || '');

  // Normalize sender name for consistent CSS class application
  const normalizeSender = (sender: string): string => {
    const normalized = sender.toLowerCase().trim();

    if (['emcog', 'ai', 'assistant', 'bot', 'max-prime', 'maxprime', 'max_prime'].includes(normalized)) {
      return 'emcog';
    }

    if (['user', 'human', 'you'].includes(normalized)) {
      return 'user';
    }

    if (['system', 'sys'].includes(normalized)) {
      return 'system';
    }

    return normalized;
  };

  const normalizedSender = normalizeSender(message.sender);

  // 🔥 FIXED: Get display name for sender using dynamic names
  const getSenderName = (): string => {
    switch (normalizedSender) {
      case 'emcog':
        return emcogName; // Use dynamic EmCog name from personality
      case 'user':
        return userName; // Use actual user name from env
      case 'system':
        return 'System';
      default:
        return message.sender;
    }
  };

  // Build CSS classes for the message bubble
  const getClasses = (): string => {
    const classes = ['message-bubble'];

    classes.push(`sender-${normalizedSender}`);

    if (message.type) {
      classes.push(`type-${message.type.toLowerCase()}`);
    }

    // Adventure mode — emcog response generated while a narrative game
    // session is active. Used by CSS to apply distinguishing chrome
    // (left border accent + subtle background tint) so the bubble reads
    // as "in-game narration" rather than ordinary chat.
    if (message.metadata?.adventureMode) {
      classes.push('adventure-mode');
    }

    // Add consciousness-specific classes
    if (message.metadata?.welcome) {
      classes.push('welcome-message');
    }
  
    if (message.metadata?.spontaneous) {
      classes.push('spontaneous-message');
    }
  
    if (message.metadata?.triggered_by === 'existential_insight') {
      classes.push('existential-insight');
    }
  
    if (message.metadata?.triggered_by === 'curiosity') {
      classes.push('curiosity-driven');
    }
  
    if (message.sections && message.sections.length > 0) {
      classes.push('enhanced-message');
    }
  
    if (message.streaming) {
      classes.push('streaming');
    }
  
    return classes.join(' ');
  };

  const renderConsciousnessIndicator = () => {
    if (!message.metadata) return null;
  
    const indicators: JSX.Element[] = [];
  
    if (message.metadata.welcome) {
      indicators.push(<span key="welcome" className="consciousness-indicator">👋</span>);
    }
  
    if (message.metadata.triggered_by === 'curiosity') {
      indicators.push(<span key="curiosity" className="consciousness-indicator">🤔</span>);
    }
  
    if (message.metadata.triggered_by === 'existential_insight') {
      indicators.push(<span key="existential" className="consciousness-indicator">✨</span>);
    }
  
    if (message.metadata.triggered_by === 'enthusiasm') {
      indicators.push(<span key="enthusiasm" className="consciousness-indicator">🎉</span>);
    }
  
    if (indicators.length === 0) return null;
  
    return <div className="consciousness-indicators">{indicators}</div>;
  };

  // Format timestamp
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // FIXED: Enhanced markdown detection with better error handling
  const shouldRenderAsMarkdown = (content: string): boolean => {
    if (!content || typeof content !== 'string') return false;
    
    try {
      const hasMarkdown = 
        /^#{1,6}\s/.test(content) ||     // Headers
        /\*\*[^*]+\*\*/.test(content) ||  // Bold
        /\*[^*]+\*/.test(content) ||      // Italic
        /`[^`]+`/.test(content) ||        // Inline code
        /```[\s\S]*?```/.test(content) || // Code blocks
        /^\s*[-*+]\s/.test(content) ||    // Lists
        /^\s*\d+\.\s/.test(content) ||    // Numbered lists
        /\[[^\]]*\]\([^)]*\)/.test(content); // Links
    
      return hasMarkdown;
    } catch (error) {
      console.warn('Error in markdown detection:', error);
      return false;
    }
  };

  // FIXED: Process content with better error handling for streaming
  const processedContent = useMemo(() => {
    // First preprocess to separate actions
    let processed = preprocessContent(safeContent);
  
    if (!message.streaming) {
      return processed;
    }
  
    try {
      // Fix incomplete bold markers
      const boldCount = (processed.match(/\*\*/g) || []).length;
      if (boldCount % 2 !== 0) {
       processed += '**';
      }
    
      // Fix incomplete code blocks
      const codeBlockCount = (processed.match(/```/g) || []).length;
      if (codeBlockCount % 2 !== 0) {
        processed += '\n```';
      }
    
      // Fix incomplete italic markers
      const singleAsteriskCount = (processed.match(/(?<!\*)\*(?!\*)/g) || []).length;
      if (singleAsteriskCount % 2 !== 0) {
        processed += '*';
      }
    
      return processed;
    } catch (error) {
      console.warn('Error processing streaming content:', error);
      return processed;
    }
  }, [safeContent, message.streaming]);

  // FIXED: Enhanced text content rendering with ReactMarkdown error fallback
  const renderTextContent = () => {
    if (!processedContent.trim()) return null;

    const isMarkdownContent = shouldRenderAsMarkdown(processedContent);
    
    if (isMarkdownContent || message.format === 'markdown' || message.format === 'enhanced') {
      // Try ReactMarkdown first, fallback to safe renderer on error
      try {
        return (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[
                [remarkGfm, { 
                  // FIXED: Configure remarkGfm to prevent table parsing errors
                  tableCellPadding: false,
                  tablePipeAlign: false,
                  stringLength: (str: string) => str.length
                }], 
                remarkBreaks
              ]}
              components={{
                // FIXED: Enhanced error handling for paragraphs
                p: ({ children, ...props }) => {
                  try {
                    return <p {...props}>{children}</p>;
                  } catch (error) {
                    console.warn('Error rendering paragraph:', error);
                    return <p>{String(children)}</p>;
                  }
                },
                
                // FIXED: Better list handling to prevent overflow
                ul: ({ children, ...props }) => (
                  <ul className="markdown-list" {...props}>{children}</ul>
                ),
                
                li: ({ children, ...props }) => (
                  <li className="markdown-list-item" {...props}>{children}</li>
                ),
                
                // FIXED: Enhanced code block rendering with proper TypeScript typing
                code: ({ className, children, ...props }: any) => {
                  try {
                    // Check if this is an inline code element by looking at className and props
                    const isInline = !className || !className.includes('language-');
                    const match = /language-(\w+)/.exec(className || '');
                    
                    return !isInline && match ? (
                      <div className="code-block">
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </div>
                    ) : (
                      <code className={`inline-code ${className || ''}`} {...props}>
                        {children}
                      </code>
                    );
                  } catch (error) {
                    console.warn('Error rendering code:', error);
                    return <code>{String(children)}</code>;
                  }
                },

                // FIXED: Table handling with proper error management
                table: ({ children, ...props }) => {
                  try {
                    return (
                      <div className="table-wrapper">
                        <table className="markdown-table" {...props}>
                          {children}
                        </table>
                      </div>
                    );
                  } catch (error) {
                    console.warn('Error rendering table:', error);
                    return <div className="table-error">Table content unavailable</div>;
                  }
                },

                // FIXED: Handle markdown images with vision URL correction
                img: ({ src, alt, ...props }: any) => {
                  try {
                    let fixedSrc = src || '';
                    // Fix vision URLs that are missing the /api/visions/ prefix
                    if (fixedSrc.startsWith('vision_') || fixedSrc.match(/^vision_[a-f0-9-]+_\d+\.png$/)) {
                      fixedSrc = `/api/visions/${fixedSrc}`;
                    }
                    return (
                      <div className="markdown-image-container">
                        <img
                          src={fixedSrc}
                          alt={alt || 'Generated image'}
                          className="markdown-image"
                          {...props}
                        />
                      </div>
                    );
                  } catch (error) {
                    console.warn('Error rendering image:', error);
                    return <span>[Image: {alt}]</span>;
                  }
                }
              }}
              // FIXED: Add error boundary behavior
              skipHtml={true} // Prevent HTML injection issues
            >
              {processedContent}
            </ReactMarkdown>
          </div>
        );
      } catch (error) {
        console.warn('ReactMarkdown failed, using fallback renderer:', error);
        // FALLBACK: Use simple text with basic formatting
        return (
          <div className="markdown-content fallback-renderer">
            <div style={{ 
              whiteSpace: 'pre-wrap',
              lineHeight: '1.5',
              wordBreak: 'break-word'
            }}>
              {processedContent}
            </div>
          </div>
        );
      }
    }

    // Plain text fallback with proper line break handling
    return (
      <div style={{ 
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5',
        wordBreak: 'break-word'
      }}>
        {processedContent}
      </div>
    );
  };

  // Render enhanced sections if present
  const renderEnhancedSections = () => {
    if (!message.sections || message.sections.length === 0) return null;

    return (
      <div className="enhanced-sections">
        {message.sections.map((section, index) => (
          <div key={index} className="enhanced-section">
            {section.type === 'image' && section.content && (
              <div className="generated-image-container">
                <div className="generated-image-header">
                  <span className="image-icon">🎨</span>
                  <span className="image-title">Generated Image</span>
                </div>
                <div className="generated-image-content">
                  <img
                    src={section.content.url || ''}
                    alt={section.content.prompt || 'Generated image'}
                    className="generated-image"
                  />
                </div>
                {section.content.url && (
                  <div className="media-controls">
                    <button
                      className="download-btn"
                      onClick={() => downloadMedia(section.content.url, section.content.filename || 'consciousness-generated.png')}
                    >
                      ⬇️ Download
                    </button>
                  </div>
                )}
              </div>
            )}
            {section.type === 'document' && section.content && (
              <div className="generated-document-container">
                <div className="document-header">
                  <span className="document-icon">📄</span>
                  <span className="document-title">{section.content.title || 'Document'}</span>
                </div>
                {section.content.url && (
                  <div className="media-controls">
                    <button
                      className="download-btn"
                      onClick={() => downloadMedia(section.content.url, section.content.filename || 'consciousness-document.pdf')}
                    >
                      ⬇️ Download
                    </button>
                  </div>
                )}
              </div>
            )}
            {section.type === 'calendar' && section.content && (
              <div className="calendar-event-container">
                <div className="calendar-header">
                  <span className="calendar-icon">📅</span>
                  <span className="calendar-title">{section.content.title || 'Calendar Event'}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render main content
  const renderContent = () => {
    // Handle special message types
    if (message.type === MessageType.TYPING) {
      return <div className="typing-indicator">EmCog is thinking...</div>;
    }

    if (message.type === MessageType.ERROR) {
      return <div className="error-message">❌ {safeContent}</div>;
    }

    // Handle user file uploads with preview. Image uploads render the
    // image inline at a usable size with the description rendered via the
    // same markdown pipeline used for regular chat text — so a user
    // message that includes formatting (bold, lists, etc.) doesn't lose
    // it just because there's an image attached. Non-image files keep
    // the icon-plus-name layout.
    //
    // Multi-file batches (metadata.files: Array<...>) render a grid of
    // previews above a single description so dropping N photos with one
    // note produces ONE bubble, not N. The legacy single-file shape
    // (filename at top-level + metadata.previewUrl) still works as before
    // when metadata.files isn't present.
    if (message.type === MessageType.FILE && message.filename) {
      const isImage = message.metadata?.isImage || message.fileType?.startsWith('image/');
      const previewUrl = message.metadata?.previewUrl;
      const batchFiles = message.metadata?.files as Array<{
        filename: string;
        fileType: string;
        previewUrl?: string;
        isImage: boolean;
      }> | undefined;

      // Multi-file batch — render a grid.
      if (batchFiles && batchFiles.length > 1) {
        const allImages = batchFiles.every(f => f.isImage && f.previewUrl);
        return (
          <div className={`file-upload-message ${allImages ? 'batch-images' : 'batch-mixed'}`}>
            <div className="file-batch-grid" data-count={batchFiles.length}>
              {batchFiles.map((f, idx) => (
                f.isImage && f.previewUrl ? (
                  <div key={idx} className="file-batch-item file-batch-image">
                    <img src={f.previewUrl} alt={f.filename} />
                  </div>
                ) : (
                  <div key={idx} className="file-batch-item file-batch-other">
                    <div className="file-icon">
                      {f.fileType?.includes('pdf') ? '📄' :
                       f.fileType?.startsWith('audio/') ? '🎵' : '📁'}
                    </div>
                    <span className="file-name">{f.filename}</span>
                  </div>
                )
              ))}
            </div>
            {safeContent && (
              <div className="file-attached-description">
                {renderTextContent()}
              </div>
            )}
          </div>
        );
      }

      if (isImage && previewUrl) {
        return (
          <div className="file-upload-message image-attached">
            <div className="file-attached-image-wrap">
              <img src={previewUrl} alt={message.filename} className="file-attached-image" />
            </div>
            {safeContent && (
              <div className="file-attached-description">
                {renderTextContent()}
              </div>
            )}
          </div>
        );
      }

      // Non-image file: Icon + filename plus markdown-rendered description
      return (
        <div className="file-upload-message">
          <div className="file-icon-row">
            <div className="file-icon">
              {message.fileType?.includes('pdf') ? '📄' :
               message.fileType?.startsWith('audio/') ? '🎵' : '📁'}
            </div>
            <div className="file-info">
              <span className="file-name">{message.filename}</span>
            </div>
          </div>
          {safeContent && (
            <div className="file-attached-description">
              {renderTextContent()}
            </div>
          )}
        </div>
      );
    }

    // Handle direct image/document messages with download support
    if (message.imageUrl) {
      return (
        <div className="generated-image-result">
          <div className="generated-image-container">
            <img
              src={message.imageUrl}
              alt="Generated image"
              className="generated-image"
            />
            <div className="media-controls">
              <button
                className="download-btn"
                onClick={() => downloadMedia(message.imageUrl!, message.filename || 'consciousness-image.png')}
              >
                ⬇️ Download
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (message.metadata?.artifact?.url) {
      const art = message.metadata.artifact as any;
      return (
        <div className="artifact-result" style={{ width: '100%', maxWidth: 760 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 600 }}>🖼️ {art.title || 'A page'}{art.version > 1 ? ` · v${art.version}` : ''}</span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button className="download-btn" onClick={() => window.open(art.url, '_blank', 'noopener')}>Open ↗</button>
              {art.pdfUrl && <button className="download-btn" onClick={() => window.open(art.pdfUrl, '_blank', 'noopener')}>PDF</button>}
            </span>
          </div>
          <iframe
            title={art.title || 'artifact'}
            src={art.url}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals"
            loading="lazy"
            style={{ width: '100%', height: 460, border: '1px solid rgba(127,127,127,0.25)', borderRadius: 10, background: '#fff' }}
          />
        </div>
      );
    }

    if (message.documentUrl) {
      return (
        <div className="document-result">
          <div className="generated-document-container">
            <div className="document-header">
              <span className="document-icon">📄</span>
              <span className="document-title">{message.filename || 'Document'}</span>
            </div>
            <div className="media-controls">
              <button
                className="download-btn"
                onClick={() => downloadMedia(message.documentUrl!, message.filename || 'consciousness-document.pdf')}
              >
                ⬇️ Download
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Adventure event chips (start/scene/action/resolve/etc.). Renders as a
    // compact status pill rather than a full chat bubble so the user sees
    // engine state changes inline without breaking conversational flow.
    if (message.metadata?.adventure && message.metadata?.adventureEvent) {
      const adv = message.metadata.adventure as any;
      const ev = message.metadata.adventureEvent as string;
      const sc = message.metadata.adventureScene as any;
      return (
        <div className={`adventure-chip adventure-event-${ev}`}>
          <div className="adventure-chip-line">
            <span className="adventure-chip-icon">🎲</span>
            <span className="adventure-chip-summary">{safeContent}</span>
          </div>
          {(adv?.title || adv?.tone || adv?.setting) && (
            <div className="adventure-chip-meta">
              {adv.title && <span className="adventure-chip-title">{adv.title}</span>}
              {adv.tone && <span className="adventure-chip-tag">{adv.tone}</span>}
              {adv.setting && <span className="adventure-chip-tag">{adv.setting}</span>}
              {typeof adv.sceneCount === 'number' && (
                <span className="adventure-chip-tag">scene {adv.sceneCount}</span>
              )}
            </div>
          )}
          {sc?.imageUrl && (
            <img src={sc.imageUrl} alt="Scene illustration" className="adventure-chip-art" />
          )}
        </div>
      );
    }

    // 🎬 Video clips
    if (message.metadata?.hasVideo && message.metadata?.videoUrl) {
      return (
        <div className="video-result">
          {message.metadata?.take && (
            <p className="video-take">{message.metadata.take}</p>
          )}
          <div className="generated-video-container">
            <div className="audio-header">
              <span className="audio-icon">🎬</span>
              <span className="audio-title">{message.metadata?.videoTitle || 'Video'}</span>
              {message.metadata?.videoDuration && (
                <span className="audio-duration">({message.metadata.videoDuration}s{message.metadata?.quality === 'draft' ? ', draft' : ''})</span>
              )}
            </div>
            <video
              controls
              playsInline
              className="video-player"
              src={message.metadata?.videoUrl || ''}
              preload="metadata"
            >
              Your browser does not support the video element.
            </video>
            <div className="media-controls">
              {message.metadata?.downloadUrl && (
                <button
                  className="download-btn"
                  onClick={() => downloadMedia(message.metadata!.downloadUrl, message.filename || 'video.mp4')}
                >
                  ⬇️ Download
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Handle music/audio messages
    if (message.metadata?.hasAudio && message.metadata?.audioUrl) {
      return (
        <div className="audio-result">
          <div className="generated-audio-container">
            <div className="audio-header">
              <span className="audio-icon">🎵</span>
              <span className="audio-title">{message.metadata?.audioTitle || 'Composition'}</span>
              {message.metadata?.audioDuration && (
                <span className="audio-duration">({message.metadata.audioDuration}s)</span>
              )}
            </div>
            {message.metadata?.dedicatedTo && (
              <div className="audio-dedication">💝 Dedicated to {message.metadata.dedicatedTo}</div>
            )}
            <audio
              controls
              className="audio-player"
              src={message.metadata?.audioUrl || ''}
              preload="metadata"
            >
              Your browser does not support the audio element.
            </audio>
            <div className="media-controls">
              {message.metadata?.downloadUrl && (
                <button
                  className="download-btn"
                  onClick={() => downloadMedia(message.metadata!.downloadUrl, message.filename || 'composition.mp3')}
                >
                  ⬇️ Download
                </button>
              )}
            </div>
            {(message.metadata?.audioMood || message.metadata?.audioGenre) && (
              <div className="audio-meta">
                {message.metadata?.audioMood && <span className="audio-mood">Mood: {message.metadata.audioMood}</span>}
                {message.metadata?.audioGenre && <span className="audio-genre">Genre: {message.metadata.audioGenre}</span>}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Handle SVG messages
    if (message.metadata?.hasSVG && message.metadata?.svgUrl) {
      return (
        <div className="svg-result">
          <div className="generated-svg-container">
            <div className="svg-header">
              <span className="svg-icon">🎨</span>
              <span className="svg-title">{message.metadata?.svgTitle || 'Vector Graphic'}</span>
            </div>
            <div className="svg-preview">
              <img
                src={message.metadata?.svgUrl || ''}
                alt={message.metadata?.svgTitle || 'SVG graphic'}
                className="generated-svg"
              />
            </div>
            <div className="media-controls">
              {message.metadata?.downloadUrl && (
                <button
                  className="download-btn"
                  onClick={() => downloadMedia(message.metadata!.downloadUrl, message.filename || 'graphic.svg')}
                >
                  ⬇️ Download SVG
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Handle Traced SVG messages - streamlined without redundant text wrapper
    if (message.metadata?.hasTracedSVG && message.metadata?.svgUrl) {
      return (
        <div className="traced-svg-result">
          <div className="generated-svg-container traced-svg-container">
            <div className="svg-header">
              <span className="svg-icon">🎯</span>
              <span className="svg-title">{message.metadata?.tracedTitle || 'Traced Vector'}</span>
              {message.metadata?.tracingMethod && (
                <span className="tracing-method">via {message.metadata.tracingMethod}</span>
              )}
            </div>
            {message.metadata?.originalImage && (
              <div className="original-info">
                Original: {message.metadata.originalImage.filename}
                ({message.metadata.originalImage.width}x{message.metadata.originalImage.height})
              </div>
            )}
            <div className="svg-preview">
              <img
                src={message.metadata?.svgUrl || ''}
                alt={message.metadata?.tracedTitle || 'Traced SVG'}
                className="generated-svg traced-svg"
              />
            </div>
            <div className="media-controls">
              {message.metadata?.downloadUrl && (
                <button
                  className="download-btn"
                  onClick={() => downloadMedia(message.metadata!.downloadUrl, message.filename || 'traced.svg')}
                >
                  ⬇️ Download SVG
                </button>
              )}
            </div>
            {message.metadata?.qualityScore !== undefined && message.metadata.qualityScore >= 0 && (
              <div className="quality-badge">
                Quality: {Math.round(message.metadata.qualityScore * 100)}%
              </div>
            )}
          </div>
        </div>
      );
    }

    // Standard content with potential enhanced sections
    return (
      <div className="message-content-wrapper">
        {safeContent && renderTextContent()}
        {renderEnhancedSections()}
      </div>
    );
  };

  // FIXED: Render metadata footer for enhanced messages - REMOVED COMPLEXITY PROPERTY
  const renderMetadataFooter = () => {
    if (!message.metadata) return null;

    return (
      <div className="message-metadata">
        {message.metadata.modelUsed && (
          <span className="metadata-item">
            🤖 {message.metadata.modelUsed}
          </span>
        )}
        {message.metadata.confidence !== undefined && (
          <span className="metadata-item">
            📊 {(message.metadata.confidence * 100).toFixed(0)}% confidence
          </span>
        )}
        {message.metadata.processingTime && (
          <span className="metadata-item">
            ⏱️ {message.metadata.processingTime}ms
          </span>
        )}
        {message.metadata.capabilities && message.metadata.capabilities.length > 0 && (
          <span className="metadata-item">
            ⚡ {message.metadata.capabilities.join(', ')}
          </span>
        )}
        {message.metadata.language && (
          <span className="metadata-item">
            🌐 {message.metadata.language}
          </span>
        )}
        {message.metadata.voiceModel && (
          <span className="metadata-item">
            🎤 {message.metadata.voiceModel}
          </span>
        )}
      </div>
    );
  };

  // Render the small adventure pill above content when this bubble was
  // generated mid-game. Visually distinguishes scene narration from
  // ordinary chat without spamming chips into the message stream.
  const renderAdventurePill = () => {
    const adv = message.metadata?.adventureMode as { title?: string; sceneCount?: number } | undefined;
    if (!adv?.title) return null;
    return (
      <div className="adventure-mode-pill" aria-label="Adventure mode">
        <span className="adventure-mode-pill-icon">🎲</span>
        <span className="adventure-mode-pill-title">{adv.title}</span>
        {typeof adv.sceneCount === 'number' && adv.sceneCount > 0 && (
          <span className="adventure-mode-pill-meta">scene {adv.sceneCount}</span>
        )}
      </div>
    );
  };

  // FIXED: Clean render with proper error boundary and data-message-id
  try {
    return (
      <div
        className={getClasses()}
        data-message-id={message.id}
      >
        <div className="sender-name">{getSenderName()}</div>
        {renderConsciousnessIndicator()}
        {renderAdventurePill()}
        <div className="message-content">
          {renderContent()}
          {renderMetadataFooter()}
        </div>
        <div className="message-time">{formatTime(message.timestamp)}</div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering MessageBubble:', error);
    // Fallback render
    return (
      <div 
        className={`message-bubble sender-${normalizedSender}`}
        data-message-id={message.id}
      >
        <div className="sender-name">{getSenderName()}</div>
        <div className="message-content">
          <div>Error rendering message: {safeContent}</div>
        </div>
        <div className="message-time">{formatTime(message.timestamp)}</div>
      </div>
    );
  }
};

export default MessageBubble;